import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { getBgGradientById } from 'eds-common';
import { useGetMeApi } from 'eds-common/api/v2';
import { useGetClassesApi } from 'eds-common/api/v3';
import { useQueries } from '@tanstack/react-query';
import { cn } from '@data/utils/cn/cn';
import { useReportOutletContext } from '@data/pages/ReportLayout/ReportLayout';
import { Metrics } from '@data/components/composite/Metrics/Metrics';
import {
  getMetricsV2Api,
  type MetricV2Type,
} from '@data/hooks/api/use-metrics-v2-api/use-metrics-v2-api';
import formatDate from '@data/utils/format-date';
import ReportBackButtonIcon from '@data/assets/reportbackbutton.svg';
import { Drilldown } from '@data/main';
import { getEdApps } from '@data/utils/get-ed-apps';
import { getSchoolYearDateRange } from '@data/utils/get-school-year';
import { Tooltip } from '@scholastic/mockingjay';
import { useGetStudentsByClassAndEdAppApi } from '@data/hooks/api/use-get-students-by-class-and-ed-app-api/use-get-students-by-class-and-ed-app-api';

interface HighlightsHeader {
  title: string;
  studentCount?: number | string;
  backTooltip: string;
}

function getStudentHeader(
  students:
    | {
        id: string;
        family_name: string;
        given_name: string;
        middle_name?: string;
      }[]
    | undefined,
  studentId: string | undefined
): HighlightsHeader {
  const student = students?.find((s) => s.id === studentId);
  const title = student
    ? [student.given_name, student.middle_name, student.family_name]
        .filter(Boolean)
        .join(' ')
    : 'Student';
  return { title, backTooltip: '' };
}

function getClassHeader(
  classes:
    | { class_id: string; name: string; studentIds: string[] }[]
    | undefined,
  classId: string,
  selectedStudentCount: number,
  entitledStudentsCount: number | string
): HighlightsHeader {
  const cls = classes?.find((c) => c.class_id === classId);
  const studentCount =
    selectedStudentCount > 0 ? selectedStudentCount : entitledStudentsCount;
  return {
    title: cls?.name ?? 'Class',
    studentCount,
    backTooltip: 'Back to All Classes',
  };
}

function getAllClassesHeader(
  classes: { class_id: string; name: string }[] | undefined,
  classIds: string[]
): HighlightsHeader {
  if (classIds.length === 0) {
    return { title: 'All Classes', backTooltip: '' };
  }
  if (classIds.length === 1) {
    const cls = classes?.find((c) => c.class_id === classIds[0]);
    return { title: cls?.name ?? '1 Class', backTooltip: '' };
  }
  return { title: `${classIds.length} Classes`, backTooltip: '' };
}

function useHighlightsHeader(): HighlightsHeader {
  const { drilldown, productId: edApp } = useReportOutletContext();
  const [searchParams] = useSearchParams();
  const { data: user } = useGetMeApi();

  const classIds = searchParams.getAll('classId');
  const studentIds = searchParams.getAll('studentId');
  const classId = classIds[0] ?? '';

  const { data: classes } = useGetClassesApi(
    {
      userId: user?.userId ?? '',
      orgId: user?.currentOrg.orgId ?? '',
      source: user?.userSource ?? 'Ednition',
      studentsExists: true,
    },
    { enabled: !!user }
  );

  // Get students entitled to the edApp to display the correct count in the header
  // Enable fetch when StudentDropdown is visible
  const { data: students } = useGetStudentsByClassAndEdAppApi(
    {
      classId: drilldown !== Drilldown.AllClasses ? classId : '',
      edApp,
    },
    {
      enabled:
        drilldown === Drilldown.Class ||
        drilldown === Drilldown.Student ||
        (classIds.length === 1 && drilldown === Drilldown.EngagementFeed),
    }
  );

  if (drilldown === Drilldown.Student) {
    const cls = classes?.find((c) => c.class_id === classId);
    const header = getStudentHeader(students, studentIds[0]);
    return { ...header, backTooltip: `Back to ${cls?.name ?? 'Class'}` };
  }

  if (drilldown === Drilldown.Class) {
    return getClassHeader(
      classes,
      classId,
      studentIds.length,
      students?.length ?? '—' // students.length will only be undefined while fetching
    );
  }

  return getAllClassesHeader(classes, classIds);
}

export { type MetricV2Type } from '@data/hooks/api/use-metrics-v2-api/use-metrics-v2-api';

interface ReportHighlightsProps {
  primaryMetricTypes?: MetricV2Type[];
  secondaryMetricTypes?: MetricV2Type[];
}

function useMetrics(metricTypes: MetricV2Type[]) {
  const { dateRange, productId } = useReportOutletContext();
  const [searchParams] = useSearchParams();
  const { data: user } = useGetMeApi();

  const classIds = searchParams.getAll('classId');
  const studentIds = searchParams.getAll('studentId');

  const results = useQueries({
    queries: metricTypes.map((metricType) => ({
      queryKey: [
        'metrics-v2',
        {
          metricType,
          orgId: user?.currentOrg.orgId ?? '',
          teacherIds: user ? [user.userId] : [],
          startDate: formatDate(dateRange.from),
          endDate: formatDate(dateRange.to, { boundary: 'end' }),
          edApps: getEdApps(productId),
          classIds: classIds.length > 0 ? classIds : null,
          studentIds: studentIds.length > 0 ? studentIds : null,
        },
      ],
      queryFn: () =>
        getMetricsV2Api({
          orgId: user?.currentOrg.orgId ?? '',
          metricType,
          teacherIds: user ? [user.userId] : [],
          startDate: dateRange.from,
          endDate: dateRange.to,
          edApps: getEdApps(productId),
          classIds: classIds.length > 0 ? classIds : null,
          studentIds: studentIds.length > 0 ? studentIds : null,
        }),
      enabled: !!user,
      staleTime: 3 * 60 * 1000,
      refetchOnWindowFocus: false,
    })),
  });

  const isLoading = results.some((q) => q.isLoading);
  const metrics = results
    .map((q) => q.data)
    .filter((d): d is NonNullable<typeof d> => !!d);

  return { metrics, isLoading };
}

function PrimaryMetricsSection({
  metricTypes,
}: {
  metricTypes: MetricV2Type[];
}) {
  const { metrics, isLoading } = useMetrics(metricTypes);

  return (
    <div className="mt-4">
      <Metrics
        metrics={metrics}
        isLoading={isLoading}
        count={metricTypes.length}
      />
    </div>
  );
}

function SecondaryMetricsSection({
  metricTypes,
}: {
  metricTypes: MetricV2Type[];
}) {
  const { metrics, isLoading } = useMetrics(metricTypes);

  return (
    <Metrics
      metrics={metrics}
      isLoading={isLoading}
      count={metricTypes.length}
      variant="secondary"
    />
  );
}

export function ReportHighlights({
  primaryMetricTypes = [],
  secondaryMetricTypes = [],
}: ReportHighlightsProps) {
  const { drilldown, setDateRange } = useReportOutletContext();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const classId = searchParams.getAll('classId')[0];
  const { title, studentCount, backTooltip } = useHighlightsHeader();

  const showGradient =
    (drilldown === Drilldown.Class || drilldown === Drilldown.Student) &&
    !!classId;
  const showBackButton =
    drilldown === Drilldown.Class || drilldown === Drilldown.Student;

  const handleBack = () => {
    if (!classId) return;
    if (drilldown === Drilldown.Student) {
      const parentPath = pathname.replace(/\/student$/, '/class');
      navigate(`${parentPath}?classId=${classId}`);
    } else if (drilldown === Drilldown.Class) {
      const parentPath = pathname.replace(/\/class$/, '');
      navigate(parentPath);
    }
    setDateRange(getSchoolYearDateRange());
  };

  return (
    <div className="overflow-hidden rounded-lg shadow-sm">
      {/* Gradient bar at class/student levels */}
      {showGradient && (
        <div
          className={cn('h-3 w-full', getBgGradientById(classId))}
          aria-hidden
        />
      )}

      {/* Title area */}
      <div className="px-4 py-6">
        <div className="mb-4 flex">
          {showBackButton && (
            <Tooltip content={backTooltip}>
              <button
                type="button"
                onClick={handleBack}
                aria-label={backTooltip}
                className="mr-2 mt-1 self-start [&:hover_svg_path:first-child]:!fill-[#E8E8E8] "
              >
                <ReportBackButtonIcon aria-hidden className="h-6 w-6" />
              </button>
            </Tooltip>
          )}
          <div>
            <h3 className="text-xl font-bold text-neutral-1300 tablet:text-2xl">
              Highlights: {title}
            </h3>
            {studentCount && (
              <p className="neutral-1300 mt-2 text-xs font-bold">
                Students
                <span className="ml-2 text-[14px] font-normal">
                  {studentCount}
                </span>
              </p>
            )}
          </div>
        </div>
        <div className="mt-4 h-[1px] w-full bg-neutral-400" />

        {/* Primary metrics */}
        {primaryMetricTypes.length > 0 && (
          <PrimaryMetricsSection metricTypes={primaryMetricTypes} />
        )}

        {/* Secondary metrics */}
        {secondaryMetricTypes.length > 0 && (
          <SecondaryMetricsSection metricTypes={secondaryMetricTypes} />
        )}
      </div>
    </div>
  );
}
